import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getAuthHeader } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';
import { openPdfInline } from '../PdfViewer/PdfViewerHost';
import './AssetDocumentsTab.css';

export const DOC_TYPES = [
  { value: 'user_guide',         label: 'User Guide' },
  { value: 'testing_spec',       label: 'Testing Spec' },
  { value: 'examination_scheme', label: 'Examination Scheme' },
  { value: 'method_statement',   label: 'Method Statement' },
  { value: 'other',              label: 'Other' },
];

const labelFor = (v) => (DOC_TYPES.find(d => d.value === v) || {}).label || 'Other';

const AssetDocumentsTab = ({ propertyId, assetType, assetId, assetName }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    file: null, name: '', docType: 'user_guide',
    requiredQualificationLevel: '', expiryDate: '',
  });

  const fetchDocs = useCallback(async () => {
    if (!propertyId || !assetType || !assetId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ assetType, assetId: String(assetId) });
      const r = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/documents?${params.toString()}`),
        { headers: getAuthHeader() }
      );
      if (!r.ok) throw new Error();
      const data = await r.json();
      setDocs(Array.isArray(data.documents) ? data.documents : []);
    } catch (_) {
      toast.error('Failed to load asset documents');
    } finally { setLoading(false); }
  }, [propertyId, assetType, assetId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.file) return toast.error('Choose a file');
    if (!form.name.trim()) return toast.error('Document name is required');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('name', form.name.trim());
      fd.append('type', labelFor(form.docType));
      fd.append('docType', form.docType);
      fd.append('assetType', assetType);
      fd.append('assetId', String(assetId));
      if (form.requiredQualificationLevel) fd.append('requiredQualificationLevel', form.requiredQualificationLevel);
      if (form.expiryDate) fd.append('expiryDate', form.expiryDate);
      const r = await fetch(getApiEndpoint(`/api/properties/${propertyId}/documents`), {
        method: 'POST',
        headers: getAuthHeader(),
        body: fd,
      });
      if (!r.ok) throw new Error();
      toast.success('Document uploaded');
      setShowUpload(false);
      setForm({ file: null, name: '', docType: 'user_guide', requiredQualificationLevel: '', expiryDate: '' });
      fetchDocs();
    } catch (_) {
      toast.error('Upload failed');
    } finally { setUploading(false); }
  };

  const handleView = async (doc) => {
    try {
      const r = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/documents/${doc.id}/download`),
        { headers: getAuthHeader() }
      );
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const isPdf = (doc.original_filename || '').toLowerCase().endsWith('.pdf') || blob.type === 'application/pdf';
      if (isPdf) {
        openPdfInline(url, doc.original_filename || doc.name);
        setTimeout(() => window.URL.revokeObjectURL(url), 5 * 60 * 1000);
      } else {
        const w = window.open(url, '_blank');
        if (w) w.onload = () => setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
    } catch (_) {
      toast.error('Could not open document');
    }
  };

  const visible = filterType === 'all' ? docs : docs.filter(d => d.doc_type === filterType);
  const counts = DOC_TYPES.reduce((acc, t) => {
    acc[t.value] = docs.filter(d => d.doc_type === t.value).length;
    return acc;
  }, {});

  return (
    <div className="cs-asset-docs">
      <div className="cs-asset-docs-header">
        <div>
          <h4 className="cs-asset-docs-title">Documents</h4>
          {assetName && <span className="cs-asset-docs-sub">for {assetName}</span>}
        </div>
        <button type="button" className="cs-asset-docs-upload" onClick={() => setShowUpload(s => !s)}>
          {showUpload ? 'Cancel' : '+ Upload document'}
        </button>
      </div>

      {showUpload && (
        <form className="cs-asset-docs-form" onSubmit={handleUpload}>
          <div className="cs-asset-docs-row">
            <label>
              <span>File</span>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                onChange={e => setForm({ ...form, file: e.target.files[0], name: form.name || (e.target.files[0]?.name || '').replace(/\.[^.]+$/, '') })}
                required
              />
            </label>
            <label>
              <span>Document type</span>
              <select value={form.docType} onChange={e => setForm({ ...form, docType: e.target.value })}>
                {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
          </div>
          <div className="cs-asset-docs-row">
            <label>
              <span>Name</span>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. SafeAnchor 5kN — User Guide"
                required
              />
            </label>
            <label>
              <span>Required qualification (optional)</span>
              <input
                type="text"
                value={form.requiredQualificationLevel}
                onChange={e => setForm({ ...form, requiredQualificationLevel: e.target.value })}
                placeholder="e.g. IRATA Level 2"
              />
            </label>
          </div>
          <div className="cs-asset-docs-row">
            <label>
              <span>Expiry date (optional)</span>
              <input
                type="date"
                value={form.expiryDate}
                onChange={e => setForm({ ...form, expiryDate: e.target.value })}
              />
            </label>
            <div className="cs-asset-docs-form-actions">
              <button type="submit" disabled={uploading}>{uploading ? 'Uploading…' : 'Save document'}</button>
            </div>
          </div>
        </form>
      )}

      <div className="cs-asset-docs-filters">
        <button
          type="button"
          className={`cs-asset-docs-chip ${filterType === 'all' ? 'active' : ''}`}
          onClick={() => setFilterType('all')}
        >All <span className="cs-asset-docs-count">{docs.length}</span></button>
        {DOC_TYPES.map(t => (
          <button
            key={t.value}
            type="button"
            className={`cs-asset-docs-chip ${filterType === t.value ? 'active' : ''}`}
            onClick={() => setFilterType(t.value)}
          >
            {t.label} <span className="cs-asset-docs-count">{counts[t.value] || 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="cs-asset-docs-state">Loading documents…</div>
      ) : visible.length === 0 ? (
        <div className="cs-asset-docs-state cs-asset-docs-empty">
          <p>No {filterType === 'all' ? '' : `${labelFor(filterType).toLowerCase()} `}documents for this asset yet.</p>
          {!showUpload && (
            <button type="button" className="cs-asset-docs-upload" onClick={() => setShowUpload(true)}>
              Upload the first one
            </button>
          )}
        </div>
      ) : (
        <ul className="cs-asset-docs-list">
          {visible.map(d => (
            <li key={d.id} className="cs-asset-docs-item">
              <div className="cs-asset-docs-item-main">
                <span className={`cs-asset-docs-tag tag-${d.doc_type || 'other'}`}>{labelFor(d.doc_type)}</span>
                <div className="cs-asset-docs-item-text">
                  <strong>{d.name}</strong>
                  <span className="cs-asset-docs-meta">
                    {d.original_filename}
                    {d.required_qualification_level && <> · Requires: {d.required_qualification_level}</>}
                    {d.expiry_date && <> · Expires {new Date(d.expiry_date).toLocaleDateString('en-GB')}</>}
                  </span>
                </div>
              </div>
              <div className="cs-asset-docs-item-actions">
                <button type="button" onClick={() => handleView(d)}>View</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AssetDocumentsTab;
